import { useState, useCallback, useRef } from "react";
import { AttachmentMeta } from "@/indexedDB/types";
import { processVoiceAttachment } from "@/middleware/attachments";

type UseVoiceAttachmentOptions = {
  messageId: string;
  messageTimestamp: string;
  chatId: string;
  attachment: AttachmentMeta;
};

export type BlobLoadState = "idle" | "loading" | "ready" | "error";

interface UseVoiceAttachmentReturn {
  blobState: BlobLoadState;
  /** Call on first play. Returns the blob or null on failure. Subsequent calls return cached blob. */
  getBlob: (signal?: AbortSignal) => Promise<Blob | null>;
}

export function useVoiceAttachment({
  messageId,
  messageTimestamp,
  chatId,
  attachment,
}: UseVoiceAttachmentOptions): UseVoiceAttachmentReturn {
  const [blobState, setBlobState] = useState<BlobLoadState>("idle");

  // Cache the resolved blob so replay is instant
  const blobCacheRef = useRef<Blob | null>(null);
  // Deduplicate in-flight calls (e.g. user taps play twice quickly)
  const inflightRef = useRef<Promise<Blob | null> | null>(null);

  const getBlob = useCallback(
    async (signal?: AbortSignal): Promise<Blob | null> => {
      // Already resolved — return immediately
      if (blobCacheRef.current) {
        return blobCacheRef.current;
      }

      // Deduplicate concurrent calls
      if (inflightRef.current) {
        return inflightRef.current;
      }

      setBlobState("loading");

      const promise = processVoiceAttachment({
        chatId,
        messageId,
        timestamp: messageTimestamp,
        attachmentMeta: attachment,
        signal,
      });

      inflightRef.current = promise;

      try {
        const blob = await promise;

        if (signal?.aborted) {
          // Don't update state if we were cancelled mid-flight
          inflightRef.current = null;
          return null;
        }

        if (!blob || blob.size === 0) {
          setBlobState("error");
          inflightRef.current = null;
          return null;
        }

        blobCacheRef.current = blob;
        setBlobState("ready");
        inflightRef.current = null;
        return blob;
      } catch {
        if (!signal?.aborted) {
          setBlobState("error");
        }
        inflightRef.current = null;
        return null;
      }
    },
    [chatId, messageId, messageTimestamp, attachment],
  );

  return { blobState, getBlob };
}
